import { formatDateTimezone, isBidPoint, isStartBidPoint, START_POINT_LETTER } from "helpers/bidpoint";
import { dateTimeToView, humanizedTimeRange } from "helpers/date";
import { bidPointTypesEnum, bidStatusesEnum, mapObjectTypesEnum, organizationFlagsEnum } from "helpers/enums";
import { pointLetter } from "helpers/system";
import React from "react";
import PointItemChange from "./pointItemChange";
import PointRefueling from "./pointRefueling";
import TimelinePoint from "./timelinePoint";

const reorderablePointTypes = [
    bidPointTypesEnum.LoadPoint,
    bidPointTypesEnum.UnloadPoint,
    bidPointTypesEnum.CustomPoint,
];

const Timeline = (props) => {
    const reorderablePointCount = props.bid.bidPoints.reduce(
        (count, bidPoint) => count + (reorderablePointTypes.includes(bidPoint.type) ? 1 : 0),
        0,
    );
    const canReorderPoints = props.canReorderBidPoints && reorderablePointCount > 1;

    const renderPoint = (point, index, lastIndex, letter) => {
        if (!point.data) {
            return null;
        }
        switch (point.type) {
            case mapObjectTypesEnum.ServicePoint:
            case mapObjectTypesEnum.BidPoint:
            case mapObjectTypesEnum.CounterpartyPoint:
            case mapObjectTypesEnum.StartBidPoint: {
                const {
                    points,
                    organization,
                    hasEstimationData,
                    estimations,
                    modal,
                    getItem,
                    canSeeEditPointDatesBtn,
                    canSeeForceCompleteBtn,
                    canSetBidEstimatedLeaveDate,
                    canSeePointPrediction,
                    bid,
                    extendedPropertiesConfig,
                    workRestInfo,
                    loadUnloadType,
                    mergeMapViewport,
                    profile,
                    enums,
                    userEntityOptions,
                    roles,
                    predictBidPoint,
                    updateBidPointsOrder,
                    hasPlanEmptyMileageBidModule,
                } = props;
                const previousPoint = points[index - 1]?.data;

                const estimation = hasEstimationData ? estimations.find((x) => x.bidPointId === point.data.id) : null;
                const { isTruckingOrganizationManager, isTruckingOrganizationHeadLogist, isTruckingOrganizationAdmin } =
                    roles;
                const forceCompleteOnLastPointManualDates =
                    lastIndex &&
                    canSeeForceCompleteBtn() &&
                    organization?.flags?.includes(organizationFlagsEnum.ForceCompleteOnLastPointManualDates);
                const pointProps = {
                    modal,
                    point: point.data,
                    first: index === 0,
                    canSeeEditPointDatesBtn: canSeeEditPointDatesBtn({ status: props.status, point: point.data }),
                    forceCompleteOnLastPointManualDates,
                    canSetBidEstimatedLeaveDate: canSetBidEstimatedLeaveDate({ point: point.data, last: lastIndex }),
                    canSetEstimatedDateAsPlanned:
                        (isTruckingOrganizationManager ||
                            isTruckingOrganizationHeadLogist ||
                            isTruckingOrganizationAdmin) &&
                        point.type === mapObjectTypesEnum.BidPoint &&
                        index !== 0 &&
                        !!estimation?.estimatedDate &&
                        [bidStatusesEnum.New, bidStatusesEnum.Planned, bidStatusesEnum.Started].includes(bid.status),
                    canPredictPoint: canSeePointPrediction({
                        point: point.data,
                        previousPointVisited:
                            !previousPoint ||
                            !!previousPoint?.autoEnteredAt ||
                            !!previousPoint?.enteredAtByLogist ||
                            !!previousPoint?.enteredAt ||
                            !!previousPoint?.manualStartAt,
                    }),
                    predictBidPoint,
                    canReorderBidPoints: canReorderPoints && reorderablePointTypes.includes(point.data.type),
                    updateBidPointsOrder,
                    hasPlanEmptyMileageBidModule,
                    letter,
                    nextPoint: points[index + 1]?.data,
                    estimatedDate: estimation ? estimation.estimatedDate : null,
                    manualEstimatedLeaveDate: estimation && lastIndex ? estimation.manualEstimatedLeaveDate : null,
                    estimationComment: estimation && lastIndex ? estimation.comment : null,
                    getItem,
                    pointsOrderedByRoute: points,
                    extendedPropertiesConfig,
                    workRestInfo,
                    loadUnloadType,
                    mergeMapViewport,
                    bid,
                    profile,
                    enums,
                    userEntityOptions,
                    roles,
                    index,
                };
                return <TimelinePoint key={index} {...pointProps} />;
            }
            case mapObjectTypesEnum.GasStation: {
                const { refuelingServiceType, modal, bid, canEditPlanRefuelings, mergeMapViewport } = props;
                return (
                    <PointRefueling
                        key={index}
                        {...point.data}
                        modal={modal}
                        getItem={props.getItem}
                        bidId={bid.id}
                        canEditPlanRefuelings={canEditPlanRefuelings}
                        organization={props.organization}
                        refuelingServiceType={refuelingServiceType}
                        mergeMapViewport={mergeMapViewport}
                        nextPoint={points[index + 1]?.data}
                    />
                );
            }
            case mapObjectTypesEnum.ItemChangePoint: {
                const { mergeMapViewport, bid } = props;
                return (
                    <PointItemChange
                        key={index}
                        {...point.data}
                        bidId={bid.id}
                        mergeMapViewport={mergeMapViewport}
                        nextPoint={points[index + 1]?.data}
                        modal={props.modal}
                        getItem={props.getItem}
                        roles={props.roles}
                    />
                );
            }
        }
    };

    const renderPoints = (points) => {
        let index = -1;
        const firstLoadPointIndex = () => {
            let i = -1;
            points.some((item) => {
                if (isBidPoint(item.type)) {
                    i++;
                }
                return [bidPointTypesEnum.LoadPoint, bidPointTypesEnum.UnloadPoint].includes(item.data?.type);
            });
            return i;
        };

        return points.map((point, i) => {
            if (isBidPoint(point.type)) {
                index++;
            }
            const curPointLetter = isStartBidPoint(point.type)
                ? START_POINT_LETTER
                : pointLetter(index, firstLoadPointIndex());
            return renderPoint(point, i, i === points.length - 1, curPointLetter);
        });
    };

    const latePeriod = (planEnterDateOffset) => {
        const { latePeriod, status } = props;
        if (!latePeriod || latePeriod.charAt(0) !== "-" || status === bidStatusesEnum.Done) {
            return null;
        }
        const utcTimezone = formatDateTimezone({ planEnterDateOffset });
        return (
            <div className="alert alert-danger">
                Опаздывает на <strong>{humanizedTimeRange(latePeriod.slice(1))}</strong> от планового времени
                прибытия&nbsp;-&nbsp;
                <strong>
                    {dateTimeToView(planEnterDateOffset)} {utcTimezone}
                </strong>
            </div>
        );
    };

    const forceCompleteInfo = (enteredAtByLogist, bid) =>
        enteredAtByLogist && (
            <div className="alert alert-info">
                <div>Заявка закрыта логистом вручную</div>
                <div className="small">
                    <div>Кем: {bid.forceCompletedBy?.shortName ? bid.forceCompletedBy.shortName : "—"}</div>
                    <div>Дата: {bid.forceCompletedAt ? dateTimeToView(bid.forceCompletedAt) : "—"}</div>
                    <div>Причина: {bid.forceCompletionReason ? bid.forceCompletionReason : "—"}</div>
                </div>
            </div>
        );

    const { points, bid } = props;
    const { planEnterDateOffset, enteredAtByLogist } = points[points.length - 1]?.data || {};
    return (
        <div className="show-bid__section pt-2">
            {latePeriod(planEnterDateOffset)}
            {forceCompleteInfo(enteredAtByLogist, bid)}
            <div className="b-timeline">{renderPoints(points)}</div>
        </div>
    );
};

export default Timeline;
